import { Utensils, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { LunchMenu } from '../../types/lunch.types';

export interface MealBarProps {
  /** If the user already ordered */
  isOrdered?: boolean;
  /** Deadline text e.g. "11:00 AM on Thursday, Nov 5" */
  deadlineText?: string;
  /** Currently selected menu */
  selectedMenu?: LunchMenu | null;
  /** Target date label */
  targetDayLabel?: string;
  /** Actions */
  onOrder?: () => void;
  onCancel?: () => void;
  isOrdering?: boolean;
  isCancelling?: boolean;
  canRequest?: boolean;
  canCancel?: boolean;
}

/**
 * Bottom action bar displaying the currently selected menu summary and the action button.
 * Matches HR Module Prototype `.meal-bar`.
 */
export const MealBar = ({
  isOrdered = false,
  deadlineText = '',
  selectedMenu,
  targetDayLabel = '',
  onOrder,
  onCancel,
  isOrdering = false,
  isCancelling = false,
  canRequest = true,
  canCancel = true,
}: MealBarProps) => {
  const { t } = useTranslation();

  if (isOrdered) {
    return (
      <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:gap-4 sm:p-5">
        <div className="flex flex-col">
          <b className="text-sm font-semibold text-foreground">
            {t('CHANGED_YOUR_MIND', { defaultValue: 'Changed your mind?' })}
          </b>
          <span className="text-xs text-muted-foreground sm:text-sm">
            {t('CANCEL_ORDER_NOTICE', {
              defaultValue: `Cancel by ${deadlineText} to order a different menu.`,
              deadline: deadlineText,
            })}
          </span>
        </div>

        {canCancel && onCancel && (
          <Button
            variant="destructive"
            onClick={onCancel}
            loading={isCancelling}
            disabled={isCancelling}
            className="w-full shrink-0 gap-1.5 sm:w-auto"
          >
            <X className="size-4" />
            {t('CANCEL_ORDER', { defaultValue: 'Cancel order' })}
          </Button>
        )}
      </div>
    );
  }

  const hasSelection = Boolean(selectedMenu);

  return (
    <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:gap-4 sm:p-5">
      <div className="flex min-w-0 flex-col">
        {hasSelection && selectedMenu ? (
          <>
            <b className="truncate text-sm font-semibold text-foreground">
              {selectedMenu.Name}
            </b>
            <span className="truncate text-xs text-muted-foreground sm:text-sm">
              {selectedMenu.MenuItems.join(', ')}
            </span>
          </>
        ) : (
          <>
            <b className="text-sm font-semibold text-foreground">
              {t('CHOOSE_A_MENU', { defaultValue: 'Choose a menu' })}
            </b>
            <span className="text-xs text-muted-foreground sm:text-sm">
              {t('PICK_SAVED_FOR', {
                defaultValue: `Your pick is saved for ${targetDayLabel}.`,
                day: targetDayLabel,
              })}
            </span>
          </>
        )}
      </div>

      {canRequest && (
        <span
          title={!hasSelection ? t('CHOOSE_MENU_FIRST', { defaultValue: 'Choose a menu first' }) : undefined}
          className="w-full sm:w-auto"
        >
          <Button
            onClick={onOrder}
            loading={isOrdering}
            disabled={!hasSelection || isOrdering}
            className="w-full shrink-0 gap-2 sm:w-auto"
          >
            <Utensils className="size-4" />
            {t('ORDER_LUNCH', { defaultValue: 'Order lunch' })}
          </Button>
        </span>
      )}
    </div>
  );
};
